import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import {
	SessionEvent,
	SessionEventKind,
	UseSessionMonitorResult,
} from '../../../hooks/health/use-session-monitor';
import { ConnectedUser } from '../../../stores/types/connection';
import { formatTimestamp } from '../format';
import Chip from '../chip';

interface Props {
	monitor: UseSessionMonitorResult;
	users: ConnectedUser[];
	selected: string | null;
	onSelect: (streamId: string | null) => void;
}

/*
	Named for what OpenTok actually reports, not for what it tends to mean. A
	connection is a client attached to the media session, which is not the same
	as a person joining the room - `joined` and `left` read as the second and
	were wrong often enough to mislead.

	The `monitor` ones are about this dashboard's own connection rather than
	anybody in the room, which the old labels gave no way to tell.
*/
const LABELS: Record<SessionEventKind, string> = {
	'connection-created': 'connected',
	'connection-destroyed': 'disconnected',
	'stream-created': 'publishing',
	'stream-destroyed': 'stopped publishing',
	'video-toggled': 'video',
	'audio-toggled': 'audio',
	'captions-toggled': 'captions',
	'resolution-changed': 'resolution',
	'session-reconnecting': 'monitor reconnecting',
	'session-reconnected': 'monitor reconnected',
	'session-disconnected': 'monitor disconnected',
	'archive-started': 'archive started',
	'archive-stopped': 'archive stopped',
	'video-quality-changed': 'video quality changed',
	'stream-video-disabled': 'video dropped',
	'stream-video-enabled': 'video restored',
	'subscriber-dropped': 'monitor feed dropped',
	'network-condition-changed': 'network condition',
	exception: 'sdk error',
};

/**
 * A drop nobody chose is a fault; a drop they did choose is not. The rest of the
 * SDK's set - `forceDisconnected`, `forceUnpublished`, `reset` - is somebody's call.
 */
const INVOLUNTARY_REASONS = [
	'networkDisconnected',
	'mediaStopped',
	// Dropped after 4 hours without publishing or subscribing.
	'mediaIdle',
	'serverRotation',
];

/**
 * The other reason a *subscriber* is given is `codecChange`, the router
 * switching codec. `bandwidth` and `cpu` belong to the publisher's own version
 * of this event, which this dashboard never sees.
 */
const DEGRADED_QUALITY_REASONS = ['videoInterruption'];

/** The other two are chosen: `publishVideo` by the publisher, `subscribeToVideo` by us. */
const VIDEO_DISABLED_FAULTS = ['quality', 'codecNotSupported'];

const isFault = (event: SessionEvent): boolean => {
	// The one kind that needs no interpretation - the SDK raised it as an error.
	if (event.kind === 'exception') return true;

	if (event.kind === 'video-quality-changed') {
		return !!event.reason && DEGRADED_QUALITY_REASONS.includes(event.reason);
	}

	if (event.kind === 'stream-video-disabled') {
		return !!event.reason && VIDEO_DISABLED_FAULTS.includes(event.reason);
	}

	// A resolution or condition change is only a fault in one direction.
	if (event.kind === 'resolution-changed' || event.kind === 'network-condition-changed') {
		return !!event.degraded;
	}

	if (event.reason && INVOLUNTARY_REASONS.includes(event.reason)) return true;

	return event.kind === 'subscriber-dropped'
		|| event.kind === 'session-reconnecting';
};

/**
 * The media server's own account of the session, as it happens.
 *
 * The client-reported panels poll each participant every few seconds and show
 * their state at those instants. This shows what happened in between: which
 * stream's resolution collapsed, and whether someone left because their network
 * dropped or because they closed the tab.
 */
export default function SessionEventsPanel({
	monitor, users, selected, onSelect,
}: Props) {
	const [faultsOnly, setFaultsOnly] = useState(false);

	// A stream carries its publisher in its name, and the monitor remembers which
	// connection it came from, so an event recorded before the name was known
	// still resolves to a person - this runs at render time.
	const { nameOf, nameOfStream } = useMemo(() => {
		const byGrUser = new Map(users.map((user) => [
			user.gr_user, `${user.first_name} ${user.last_name}`.trim(),
		]));
		// The monitor's own map rather than one built from the live stream list:
		// most events carry only a streamId, and the list is pruned the moment a
		// stream ends - which is when the events worth reading are recorded.
		const byStream = monitor.streamIdentities;
		const nameFor = (grUser?: number) => (grUser ? byGrUser.get(grUser) : undefined);
		return {
			nameOfStream: (streamId: string) => nameFor(byStream.get(streamId)),
			nameOf: (event: SessionEvent) => nameFor(
				(event.streamId ? byStream.get(event.streamId) : undefined)
				?? (event.connectionId ? monitor.identities.get(event.connectionId) : undefined),
			// A SIP caller is in no roster, so its own connection data is the only
			// thing that will ever name it.
			) ?? (event.connectionId ? monitor.connectionNames.get(event.connectionId) : undefined),
		};
	}, [users, monitor.streamIdentities, monitor.identities, monitor.connectionNames]);

	// Scoped before counting: with one publisher picked, a fault count for the
	// whole session would not match the rows underneath it.
	const scoped = useMemo(
		() => (selected
			? monitor.events.filter((event) => event.streamId === selected)
			: monitor.events),
		[monitor.events, selected],
	);
	const faultCount = useMemo(() => scoped.filter(isFault).length, [scoped]);
	const visible = useMemo(
		() => (faultsOnly ? scoped.filter(isFault) : scoped),
		[scoped, faultsOnly],
	);

	return (
		<section className='health-panel'>
			<header className='health-panel__header'>
				<div>
					<h3 className='health-panel__title'>Session events</h3>
					<span className='health-panel__source'>opentok</span>
				</div>
				<div className='health-panel__status'>
					<span className={classNames('health-pill', monitor.connected ? 'health-pill--ok' : 'health-pill--empty')}>
						{monitor.connected ? `${monitor.connectionCount} connected` : 'not connected'}
					</span>
				</div>
			</header>

			<div className='health-panel__body'>
				{!!monitor.error && (
					<p className='health-banner health-banner--error'>
						Could not monitor the session: {monitor.error}
					</p>
				)}

				{monitor.unmarked && (
					<p className='health-banner health-banner--warn'>
						The session token came back without this dashboard&apos;s marker, so other
						clients cannot tell this connection from a participant&apos;s.
					</p>
				)}

				{!monitor.error && !monitor.connected && (
					<p className='health-panel__note'>Connecting to the session…</p>
				)}

				{monitor.connected && !!monitor.events.length && !scoped.length && (
					<p className='health-panel__note'>
						Nothing has happened to {nameOfStream(selected ?? '') ?? 'this publisher'} yet.
						{' '}
						<button type='button' className='health-link-button' onClick={() => onSelect(null)}>
							Show the whole session
						</button>
					</p>
				)}

				{monitor.connected && !monitor.events.length && (
					<p className='health-panel__note'>
						Nothing has happened in the session since this tab was opened.
					</p>
				)}

				{!!scoped.length && (
					<>
						<div className='health-chips'>
							<button
								type='button'
								className='health-chip-button'
								onClick={() => setFaultsOnly(false)}
							>
								<Chip label='all' value={scoped.length} selected={!faultsOnly} />
							</button>
							{!!selected && (
								<Chip
									label={nameOfStream(selected) ?? 'publisher'}
									value={scoped.length}
									note='filtered'
								/>
							)}
							<button
								type='button'
								className='health-chip-button'
								onClick={() => setFaultsOnly((previous) => !previous)}
							>
								<Chip
									label='faults'
									value={faultCount}
									tone={faultCount ? 'warn' : undefined}
									note={faultsOnly ? 'filtered' : undefined}
									selected={faultsOnly}
								/>
							</button>
						</div>

						<div className='health-scroll'>
							<table className='health-table'>
								<thead>
									<tr>
										<th>Time</th>
										<th>Who</th>
										<th>Event</th>
										<th>Detail</th>
									</tr>
								</thead>
								<tbody>
									{visible.map((event) => (
										<tr key={event.seq}>
											<td className='health-mono'>{formatTimestamp(event.at)}</td>
											<td>{nameOf(event) ?? <span className='health-empty'>—</span>}</td>
											<td>
												<div className='health-tagged'>
													<span className={classNames('health-tag', { 'health-tag--warn': isFault(event) })}>
														{LABELS[event.kind]}
													</span>
													{typeof event.code === 'number' && (
														<span className='health-tag health-tag--warn'>{event.code}</span>
													)}
												</div>
											</td>
											<td>{event.reason ?? event.detail ?? '—'}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
